import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
    Building2, 
    Plus, 
    RefreshCw, 
    Trash2, 
    CreditCard,
    ArrowUpRight,
    ArrowDownLeft,
    AlertCircle,
    CheckCircle2,
    Loader2,
    Search,
    ChevronRight,
    ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../lib/api';

// Formater un montant en euros
const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR'
    }).format(amount);
};

// Composant carte compte bancaire
const BankAccountCard = ({ account, onRefresh, onViewTransactions }) => {
    const [refreshing, setRefreshing] = useState(false);
    
    const handleRefresh = async () => {
        setRefreshing(true);
        await onRefresh(account.account_id);
        setRefreshing(false);
    };
    
    return (
        <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                            <CreditCard className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                            <h3 className="font-medium">{account.account_name || 'Compte'}</h3>
                            <p className="text-sm text-muted-foreground font-mono">
                                {account.iban ? `${account.iban.slice(0, 4)} •••• ${account.iban.slice(-4)}` : 'IBAN non disponible'}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                                {account.institution_name}
                            </p>
                        </div>
                    </div>
                    
                    <div className="text-right">
                        <p className="text-2xl font-bold">
                            {formatCurrency(account.current_balance)}
                        </p>
                        {account.available_balance !== account.current_balance && (
                            <p className="text-sm text-muted-foreground">
                                Disponible: {formatCurrency(account.available_balance)}
                            </p>
                        )}
                    </div>
                </div>
                
                <div className="flex gap-2 mt-4 pt-3 border-t">
                    <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleRefresh}
                        disabled={refreshing}
                    >
                        {refreshing ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <RefreshCw className="w-4 h-4" />
                        )}
                        <span className="ml-1">Actualiser</span>
                    </Button>
                    <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => onViewTransactions(account)}
                    >
                        <ChevronRight className="w-4 h-4" />
                        <span className="ml-1">Transactions</span>
                    </Button>
                </div>
                
                {account.updated_at && (
                    <p className="text-xs text-muted-foreground mt-2">
                        Mis à jour: {new Date(account.updated_at).toLocaleString('fr-FR')}
                    </p>
                )}
            </CardContent>
        </Card>
    );
};

// Dialog pour connecter une banque
const ConnectBankDialog = ({ open, onOpenChange, onSuccess }) => {
    const [institutions, setInstitutions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [search, setSearch] = useState('');
    
    useEffect(() => {
        if (open) {
            loadInstitutions();
        }
    }, [open]);
    
    const loadInstitutions = async () => {
        setLoading(true);
        try {
            const response = await api.get('/bank/institutions');
            setInstitutions(response.data.institutions || []);
        } catch (error) {
            toast.error("Impossible de charger les banques");
        } finally {
            setLoading(false);
        }
    };
    
    const handleConnect = async (institution) => {
        setConnecting(true);
        try {
            const response = await api.post('/bank/connect', {
                institution_id: institution.id,
                institution_name: institution.name
            });
            
            const { requisition_id, redirect_url } = response.data;
            
            // Sauvegarder l'ID pour le callback
            localStorage.setItem('pending_bank_requisition', requisition_id);
            
            // Rediriger vers la banque
            window.location.href = redirect_url;
            
        } catch (error) {
            toast.error("Impossible d'initier la connexion");
            setConnecting(false);
        }
    };
    
    const filteredInstitutions = institutions.filter(inst =>
        inst.name.toLowerCase().includes(search.toLowerCase()) ||
        (inst.bic && inst.bic.toLowerCase().includes(search.toLowerCase()))
    );
    
    // Séparer les banques principales des autres
    const mainBanks = filteredInstitutions.filter(i => i.is_main);
    const otherBanks = filteredInstitutions.filter(i => !i.is_main);
    
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Building2 className="w-5 h-5" />
                        Connecter une banque
                    </DialogTitle>
                </DialogHeader>
                
                <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Rechercher une banque..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                    />
                </div>
                
                {connecting && (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        <span className="ml-2">Redirection vers votre banque...</span>
                    </div>
                )}
                
                {!connecting && loading && (
                    <div className="space-y-2">
                        {[1, 2, 3, 4, 5].map(i => (
                            <Skeleton key={i} className="h-14 w-full" />
                        ))}
                    </div>
                )}
                
                {!connecting && !loading && (
                    <div className="overflow-y-auto flex-1 space-y-4">
                        {mainBanks.length > 0 && (
                            <div>
                                <h4 className="text-sm font-medium text-muted-foreground mb-2">
                                    Banques principales
                                </h4>
                                <div className="space-y-1">
                                    {mainBanks.map(inst => (
                                        <button
                                            key={inst.id}
                                            onClick={() => handleConnect(inst)}
                                            className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-muted transition-colors text-left"
                                        >
                                            {inst.logo ? (
                                                <img 
                                                    src={inst.logo} 
                                                    alt={inst.name}
                                                    className="w-8 h-8 object-contain"
                                                />
                                            ) : (
                                                <Building2 className="w-8 h-8 text-muted-foreground" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium truncate">{inst.name}</p>
                                                <p className="text-xs text-muted-foreground">{inst.bic}</p>
                                            </div>
                                            <ExternalLink className="w-4 h-4 text-muted-foreground" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {otherBanks.length > 0 && (
                            <div>
                                <h4 className="text-sm font-medium text-muted-foreground mb-2">
                                    Autres banques ({otherBanks.length})
                                </h4>
                                <div className="space-y-1">
                                    {otherBanks.slice(0, 20).map(inst => (
                                        <button
                                            key={inst.id}
                                            onClick={() => handleConnect(inst)}
                                            className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-muted transition-colors text-left"
                                        >
                                            {inst.logo ? (
                                                <img 
                                                    src={inst.logo} 
                                                    alt={inst.name}
                                                    className="w-8 h-8 object-contain"
                                                />
                                            ) : (
                                                <Building2 className="w-8 h-8 text-muted-foreground" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium truncate">{inst.name}</p>
                                                <p className="text-xs text-muted-foreground">{inst.bic}</p>
                                            </div>
                                            <ExternalLink className="w-4 h-4 text-muted-foreground" />
                                        </button>
                                    ))}
                                    {otherBanks.length > 20 && (
                                        <p className="text-sm text-muted-foreground text-center py-2">
                                            ... et {otherBanks.length - 20} autres banques
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        {filteredInstitutions.length === 0 && (
                            <div className="text-center py-8 text-muted-foreground">
                                <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                <p>Aucune banque trouvée</p>
                            </div>
                        )}
                    </div>
                )}
                
                <p className="text-xs text-muted-foreground text-center mt-4 pt-4 border-t">
                    Connexion sécurisée via GoCardless (Open Banking PSD2)
                </p>
            </DialogContent>
        </Dialog>
    );
};

// Page principale des comptes bancaires
const BankAccountsPage = () => {
    const [accounts, setAccounts] = useState([]);
    const [totalBalance, setTotalBalance] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isConfigured, setIsConfigured] = useState(false);
    const [showConnectDialog, setShowConnectDialog] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loadingTransactions, setLoadingTransactions] = useState(false);
    
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // Charger les comptes
    const loadAccounts = useCallback(async () => {
        try {
            const response = await api.get('/bank/accounts');
            setAccounts(response.data.accounts || []);
            setTotalBalance(response.data.total_balance || 0);
        } catch (error) {
            console.error('Erreur chargement comptes:', error);
        } finally {
            setLoading(false);
        }
    }, []);
    
    // Vérifier si l'intégration est configurée
    const checkStatus = useCallback(async () => {
        try {
            const response = await api.get('/bank/status');
            setIsConfigured(response.data.configured);
        } catch (error) {
            console.error('Erreur status:', error);
        }
    }, []);
    
    // Gérer le callback de la banque
    const handleCallback = useCallback(async () => {
        const requisitionId = localStorage.getItem('pending_bank_requisition');
        if (!requisitionId) return;
        
        try {
            toast.info("Récupération de vos comptes bancaires...");
            
            const response = await api.post(`/bank/sync/${requisitionId}`);
            
            if (response.data.status === 'LN' || response.data.accounts_synced > 0) {
                toast.success(`${response.data.accounts_synced} compte(s) connecté(s)`);
                loadAccounts();
            } else {
                toast.warning("La connexion n'est pas encore finalisée");
            }
        } catch (error) {
            toast.error("Impossible de finaliser la connexion");
        } finally {
            localStorage.removeItem('pending_bank_requisition');
            // Nettoyer l'URL
            navigate('/bank', { replace: true });
        }
    }, [loadAccounts, navigate]);
    
    useEffect(() => {
        checkStatus();
        loadAccounts();
        
        // Vérifier si on revient d'un callback banque
        if (searchParams.get('ref') || localStorage.getItem('pending_bank_requisition')) {
            handleCallback();
        }
    }, [checkStatus, loadAccounts, handleCallback, searchParams]);
    
    // Rafraîchir un compte
    const handleRefresh = async (accountId) => {
        try {
            await api.post(`/bank/accounts/${accountId}/refresh`);
            loadAccounts();
            toast.success("Solde actualisé");
        } catch (error) {
            toast.error("Impossible d'actualiser le solde");
        }
    };
    
    // Voir les transactions
    const handleViewTransactions = async (account) => {
        setSelectedAccount(account);
        setLoadingTransactions(true);
        
        try {
            const response = await api.get(`/bank/accounts/${account.account_id}/transactions`);
            setTransactions(response.data.booked || []);
        } catch (error) {
            toast.error("Impossible de charger les transactions");
        } finally {
            setLoadingTransactions(false);
        }
    };
    
    if (!isConfigured) {
        return (
            <div className="container mx-auto p-6">
                <Card className="max-w-xl mx-auto">
                    <CardContent className="p-8 text-center">
                        <AlertCircle className="w-16 h-16 mx-auto mb-4 text-amber-500" />
                        <h2 className="text-xl font-semibold mb-2">Intégration bancaire non configurée</h2>
                        <p className="text-muted-foreground mb-4">
                            Pour connecter vos comptes bancaires, vous devez d'abord configurer les clés API Nordigen.
                        </p>
                        <div className="bg-muted p-4 rounded-lg text-left text-sm">
                            <p className="font-medium mb-2">Étapes :</p>
                            <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                                <li>Créer un compte sur <a href="https://bankaccountdata.gocardless.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">bankaccountdata.gocardless.com</a></li>
                                <li>Générer des clés API (User Secrets)</li>
                                <li>Ajouter NORDIGEN_SECRET_ID et NORDIGEN_SECRET_KEY dans le fichier .env</li>
                                <li>Redémarrer l'application</li>
                            </ol>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }
    
    return (
        <div className="container mx-auto p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Building2 className="w-7 h-7" />
                        Comptes Bancaires
                    </h1>
                    <p className="text-muted-foreground">
                        Gérez vos connexions bancaires et suivez vos soldes
                    </p>
                </div>
                <Button onClick={() => setShowConnectDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Connecter une banque
                </Button>
            </div>
            
            {/* Solde total */}
            <Card className="bg-gradient-to-r from-primary/10 to-primary/5">
                <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-muted-foreground">Solde total</p>
                            <p className="text-4xl font-bold">{formatCurrency(totalBalance)}</p>
                            <p className="text-sm text-muted-foreground mt-1">
                                {accounts.length} compte(s) connecté(s)
                            </p>
                        </div>
                        <Button variant="outline" onClick={loadAccounts}>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Actualiser tout
                        </Button>
                    </div>
                </CardContent>
            </Card>
            
            {/* Liste des comptes */}
            {loading ? (
                <div className="grid gap-4 md:grid-cols-2">
                    {[1, 2].map(i => (
                        <Skeleton key={i} className="h-40" />
                    ))}
                </div>
            ) : accounts.length === 0 ? (
                <Card>
                    <CardContent className="p-8 text-center">
                        <CreditCard className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                        <h3 className="text-lg font-medium mb-2">Aucun compte connecté</h3>
                        <p className="text-muted-foreground mb-4">
                            Connectez votre première banque pour voir vos soldes ici
                        </p>
                        <Button onClick={() => setShowConnectDialog(true)}>
                            <Plus className="w-4 h-4 mr-2" />
                            Connecter une banque
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4 md:grid-cols-2">
                    {accounts.map(account => (
                        <BankAccountCard
                            key={account.account_id}
                            account={account}
                            onRefresh={handleRefresh}
                            onViewTransactions={handleViewTransactions}
                        />
                    ))}
                </div>
            )}
            
            {/* Dialog connexion banque */}
            <ConnectBankDialog
                open={showConnectDialog}
                onOpenChange={setShowConnectDialog}
                onSuccess={loadAccounts}
            />
            
            {/* Dialog transactions */}
            <Dialog open={!!selectedAccount} onOpenChange={() => setSelectedAccount(null)}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle>
                            Transactions - {selectedAccount?.account_name || selectedAccount?.iban}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {loadingTransactions ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-8 h-8 animate-spin" />
                        </div>
                    ) : transactions.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <p>Aucune transaction récente</p>
                        </div>
                    ) : (
                        <div className="overflow-y-auto flex-1 space-y-2">
                            {transactions.slice(0, 50).map((tx, idx) => (
                                <div 
                                    key={idx}
                                    className="flex items-center justify-between p-3 rounded-lg border"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-full ${
                                            parseFloat(tx.transactionAmount?.amount || 0) >= 0 
                                                ? 'bg-green-100 text-green-600' 
                                                : 'bg-red-100 text-red-600'
                                        }`}>
                                            {parseFloat(tx.transactionAmount?.amount || 0) >= 0 
                                                ? <ArrowDownLeft className="w-4 h-4" />
                                                : <ArrowUpRight className="w-4 h-4" />
                                            }
                                        </div>
                                        <div>
                                            <p className="font-medium text-sm">
                                                {tx.creditorName || tx.debtorName || tx.remittanceInformationUnstructured || 'Transaction'}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {tx.bookingDate}
                                            </p>
                                        </div>
                                    </div>
                                    <div className={`font-semibold ${
                                        parseFloat(tx.transactionAmount?.amount || 0) >= 0 
                                            ? 'text-green-600' 
                                            : 'text-red-600'
                                    }`}>
                                        {formatCurrency(parseFloat(tx.transactionAmount?.amount || 0))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default BankAccountsPage;
